<template>
  <v-dialog v-model="internalValue" max-width="450" class="glass-dialog">
    <v-card class="rounded-xl border border-white-10 bg-glass backdrop-blur overflow-hidden text-white">
      <v-card-title class="pa-5 font-weight-black d-flex align-center">
        <v-icon icon="mdi-playlist-plus" class="mr-3" color="primary"></v-icon>
        <span class="text-white">Add to Lists</span>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" size="small" color="white" @click="internalValue = false"></v-btn>
      </v-card-title>

      <v-divider class="border-white-10"></v-divider>

      <div class="pa-5">
        <div class="d-flex align-center mb-6 pa-4 rounded-lg bg-white-05 border border-white-05">
          <v-avatar size="44" class="mr-3 border border-primary">
            <v-img v-if="actorAvatar" :src="actorAvatar" cover></v-img>
            <v-icon v-else icon="mdi-account" color="white"></v-icon>
          </v-avatar>
          <div>
            <div class="text-subtitle-2 font-weight-black text-white">@{{ actorHandle }}</div>
            <div class="text-caption text-grey-lighten-1">Manage list membership</div>
          </div>
        </div>

        <div v-if="loading" class="text-center py-10">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <div class="mt-2 text-caption text-grey-lighten-2">Fetching your lists...</div>
        </div>

        <div v-else-if="lists.length === 0" class="text-center py-8">
          <v-icon icon="mdi-playlist-remove" size="48" color="grey" class="mb-2"></v-icon>
          <div class="text-body-2 text-grey-lighten-2 mb-4">You don't have any lists yet.</div>
          <v-btn color="primary" variant="flat" rounded="pill" prepend-icon="mdi-plus" @click="showCreateField = true">
            Create your first list
          </v-btn>
        </div>

        <div v-else class="lists-container mb-4">
          <v-list bg-color="transparent" class="pa-0">
            <v-list-item
              v-for="list in lists"
              :key="list.uri"
              class="rounded-lg mb-2 list-item-action text-white"
              @click="toggleMembership(list)"
            >
              <template v-slot:prepend>
                <v-avatar size="36" color="grey-darken-4" class="mr-3 border border-white-05">
                  <v-img v-if="list.avatar" :src="list.avatar" cover>
                    <template v-slot:placeholder>
                      <div class="d-flex align-center justify-center fill-height">
                        <v-progress-circular indeterminate size="16" width="2" color="primary"></v-progress-circular>
                      </div>
                    </template>
                    <template v-slot:error>
                      <v-icon icon="mdi-playlist-play" size="small" color="white"></v-icon>
                    </template>
                  </v-img>
                  <v-icon v-else icon="mdi-playlist-play" size="small" color="white"></v-icon>
                </v-avatar>
              </template>
              
              <v-list-item-title class="text-body-2 font-weight-bold text-white">{{ list.name }}</v-list-item-title>
              
              <template v-slot:append>
                <v-progress-circular v-if="processingUris.has(list.uri)" indeterminate size="20" width="2" color="primary"></v-progress-circular>
                <v-icon
                  v-else
                  :icon="isMember(list.uri) ? 'mdi-check-circle' : 'mdi-plus-circle-outline'"
                  :color="isMember(list.uri) ? 'primary' : 'grey-lighten-1'"
                ></v-icon>
              </template>
            </v-list-item>
          </v-list>
        </div>

        <div v-if="showCreateField" class="mt-4">
          <v-text-field
            v-model="newListName"
            placeholder="New list name"
            variant="outlined"
            density="compact"
            hide-details
            class="rounded-lg mb-3 custom-input"
            autofocus
            @keyup.enter="handleCreateList"
          ></v-text-field>
          <div class="d-flex gap-2">
            <v-btn variant="text" size="small" color="grey-lighten-1" @click="showCreateField = false; newListName = ''">Cancel</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" size="small" variant="flat" :loading="creating" :disabled="!newListName.trim()" @click="handleCreateList">Create</v-btn>
          </div>
        </div>
        <v-btn
          v-else-if="lists.length > 0"
          block
          variant="tonal"
          prepend-icon="mdi-plus"
          class="mt-2 text-none rounded-lg border border-white-10"
          color="primary"
          @click="showCreateField = true"
        >
          Create new list
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { 
  getUserLists, 
  getListMembers, 
  createNativeList, 
  addActorToList, 
  removeActorFromList,
  currentUser
} from '@/services/bluesky';

const props = defineProps<{
  modelValue: boolean;
  actorDid: string;
  actorHandle: string;
  actorAvatar?: string;
}>();

const emit = defineEmits(['update:modelValue']);

const internalValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const loading = ref(true);
const creating = ref(false);
const showCreateField = ref(false);
const newListName = ref('');
const lists = ref<any[]>([]);
const membershipMap = ref<Map<string, string>>(new Map()); // listUri -> listItemUri
const processingUris = ref<Set<string>>(new Set());

const isMember = (listUri: string) => membershipMap.value.has(listUri);

const fetchAll = async () => {
  if (!props.modelValue) return;
  loading.value = true;
  try {
    const { lists: userLists } = await getUserLists();
    lists.value = userLists;
    
    // For each list, check if actor is a member
    const memberships = new Map<string, string>();
    
    // Parallel check for membership
    await Promise.all(userLists.map(async (list: any) => {
      try {
        const { items } = await getListMembers(list.uri);
        const item = items.find((i: any) => i.subject.did === props.actorDid);
        if (item) {
          memberships.set(list.uri, item.uri);
        }
      } catch (e) {
        console.error(`Failed to check membership for ${list.name}`, e);
      }
    }));
    
    membershipMap.value = memberships;
  } catch (error) {
    console.error('Failed to fetch lists:', error);
  } finally {
    loading.value = false;
  }
};

const toggleMembership = async (list: any) => {
  if (processingUris.value.has(list.uri)) return;
  
  const currentItemUri = membershipMap.value.get(list.uri);
  processingUris.value.add(list.uri);
  
  try {
    if (currentItemUri) {
      // Remove
      await removeActorFromList(currentItemUri);
      membershipMap.value.delete(list.uri);
    } else {
      // Add
      const res = await addActorToList(list.uri, props.actorDid);
      membershipMap.value.set(list.uri, res.uri);
    }
  } catch (error) {
    console.error('Failed to toggle membership:', error);
  } finally {
    processingUris.value.delete(list.uri);
  }
};

const handleCreateList = async () => {
  const name = newListName.value.trim();
  if (!name) return;
  
  creating.value = true;
  try {
    await createNativeList(name);
    newListName.value = '';
    showCreateField.value = false;
    await fetchAll(); // Refresh
  } catch (error) {
    console.error('Failed to create list:', error);
  } finally {
    creating.value = false;
  }
};

watch(() => props.modelValue, (val) => {
  if (val) fetchAll();
});
</script>

<style scoped>
.backdrop-blur {
  backdrop-filter: blur(40px) saturate(180%);
  -webkit-backdrop-filter: blur(40px) saturate(180%);
}
.bg-glass {
  background: rgba(15, 15, 15, 0.7) !important;
}
.border-white-10 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
.bg-white-05 {
  background: rgba(255, 255, 255, 0.05) !important;
}
.lists-container {
  max-height: 350px;
  overflow-y: auto;
  padding-right: 4px;
}
.list-item-action {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid transparent;
}
.list-item-action:hover {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.1);
  transform: translateY(-1px);
}

.custom-input :deep(.v-field) {
  background: rgba(255, 255, 255, 0.05) !important;
  border-radius: 12px;
}

/* Scrollbar */
.lists-container::-webkit-scrollbar {
  width: 4px;
}
.lists-container::-webkit-scrollbar-track {
  background: transparent;
}
.lists-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

:deep(.v-dialog > .v-overlay__content) {
  border-radius: 24px;
  overflow: hidden;
}
</style>
